// ============================================================================
// Índice público de um space: /kb/:tenantSlug/:spaceSlug
// Lista todas as páginas públicas do space.
// F3 Bloco A — Wiki pública.
// ============================================================================

import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FileText, Loader2 } from "lucide-react";
import {
  useKbPublicTenant,
  useKbPublicSpaces,
  useKbPublicPages,
} from "@/hooks/kb/useKbPublic";
import { KbPublicLayout } from "./KbPublicLayout";
import { Card, CardContent } from "@/components/ui/card";

export default function KbPublicSpacePage() {
  const { tenantSlug = "", spaceSlug = "" } = useParams<{
    tenantSlug: string;
    spaceSlug: string;
  }>();

  const { data: tenant } = useKbPublicTenant(tenantSlug);
  const { data: spaces = [] } = useKbPublicSpaces(tenantSlug);
  const { data: pages = [], isLoading, error } = useKbPublicPages(tenantSlug, spaceSlug);

  const space = useMemo(
    () => spaces.find((s) => s.space_slug === spaceSlug),
    [spaces, spaceSlug],
  );

  const canonical = `${window.location.origin}/kb/${tenantSlug}/${spaceSlug}`;
  const title = space
    ? `${space.space_name} — ${tenant?.tenant_name ?? "Base de Conhecimento"}`
    : "Base de Conhecimento";
  const description = space?.description
    ? space.description
    : `Documentos públicos do espaço ${space?.space_name ?? spaceSlug}.`;

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: title,
    url: canonical,
    description,
  };

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        {!space && <meta name="robots" content="noindex" />}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <KbPublicLayout
        tenantSlug={tenantSlug}
        tenantName={tenant?.tenant_name}
        breadcrumbs={[
          { label: "Início", to: `/kb/${tenantSlug}` },
          { label: space?.space_name ?? spaceSlug },
        ]}
      >
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight">
            {space?.space_name ?? spaceSlug}
          </h1>
          {space?.description && (
            <p className="mt-2 text-muted-foreground">{space.description}</p>
          )}
        </div>

        {error ? (
          <div className="rounded-lg border border-border bg-muted/30 p-8 text-center">
            <p className="text-muted-foreground">Espaço não encontrado.</p>
          </div>
        ) : pages.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-10 text-center text-muted-foreground">
            Nenhum artigo público neste espaço.
          </div>
        ) : (
          <ul className="space-y-3">
            {pages.map((p) => (
              <li key={p.page_id}>
                <Link
                  to={`/kb/${tenantSlug}/${spaceSlug}/${p.page_slug}`}
                  className="block"
                >
                  <Card className="transition-shadow hover:shadow-md">
                    <CardContent className="flex items-start gap-3 py-4">
                      <FileText
                        className="mt-0.5 h-5 w-5 shrink-0 text-primary"
                        aria-hidden
                      />
                      <div className="min-w-0 flex-1">
                        <h2 className="font-medium leading-tight">{p.title}</h2>
                        {p.summary && (
                          <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                            {p.summary}
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </KbPublicLayout>
    </>
  );
}
